import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Button, HStack, Icon, Stat, StatLabel, StatNumber, Flex } from "@chakra-ui/react";
import { FaHome, FaChartLine, FaSyncAlt, FaCar, FaCheckCircle, FaArrowRight, FaHandHoldingUsd } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { GoogleReviews } from "../../../components/chakra/GoogleReviews/GoogleReviews";

const FINANCE_PRIMARY = "#141C49";
const FINANCE_ACCENT = "#C2A878";
const NAVY = "#141C49";

const FinanceHome: React.FC = () => {
  const serviceCategories = [
    {
      icon: FaHome,
      title: "Mortgage",
      description: "Your independent lending expert. We search 40+ lenders to find the right home loan for your situation — free to you.",
      color: FINANCE_ACCENT,
      features: ["First Home Buyers", "Owner-Occupier & Investment Loans", "Construction Loans", "Reverse Mortgage"],
      img: "/assets/finance-assets/asset 5.jpeg",
      imgAlt: "Mortgage broker - home loan advice",
      link: "/finance/services/mortgage"
    },
    {
      icon: FaChartLine,
      title: "Investment",
      description: "Build wealth through property. From investment loans to SMSF strategies — we help you grow your portfolio.",
      color: FINANCE_ACCENT,
      features: ["Investment Property", "SMSF Loans", "SMSF Setup Support", "Portfolio Strategy"],
      img: "/assets/finance-assets/asset 6.jpeg",
      imgAlt: "Investment property finance",
      link: "/finance/services/investment"
    },
    {
      icon: FaSyncAlt,
      title: "Refinancing",
      description: "Is your interest rate high? We compare the market and make switching simple — with no upfront cost to you.",
      color: FINANCE_ACCENT,
      features: ["Lower Your Rate", "Access Equity", "Debt Consolidation", "Better Features"],
      img: "/assets/finance-assets/asset 7.jpeg",
      imgAlt: "Home loan refinancing",
      link: "/finance/services/refinancing"
    },
    {
      icon: FaCar,
      title: "Assets",
      description: "Car loans, caravan finance, equipment and business assets. Fast approvals, competitive rates.",
      color: FINANCE_ACCENT,
      features: ["Car Loans", "Caravan & Boats", "Equipment Finance", "Business Assets"],
      img: "/assets/finance-assets/asset 17.jpeg",
      imgAlt: "Asset finance - car and equipment loans",
      link: "/finance/services/assets"
    },
    {
      icon: FaHandHoldingUsd,
      title: "Personal",
      description: "Flexible personal loans from $3,000 to $80,000. Debt consolidation, renovations, travel, and more.",
      color: FINANCE_ACCENT,
      features: ["Debt Consolidation", "Home Renovations", "Deposit on Land Contract", "Fast 24hr Approval"],
      img: "/assets/finance-assets/asset 13.jpeg",
      imgAlt: "Personal loans for any life goal",
      link: "/finance/services/personal"
    }
  ];

  const benefits = [
    "Access to 40+ lenders",
    "Competitive interest rates",
    "Expert broker advice",
    "Fast pre-approvals",
    "No upfront fees",
    "Ongoing support"
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance"
        fallbackTitle="Finance Solutions"
        fallbackDescription="Home loans, refinancing, investment property, and wealth management services"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 24 }}
        backgroundImage="url('/assets/finance-assets/asset%2018.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color={FINANCE_ACCENT} textTransform="uppercase" letterSpacing="wider" fontWeight="600">
              Finance &amp; Wealth Management
            </Text>
            <Heading size="2xl" fontWeight="bold" maxW="4xl">
              Finance Solutions for Every Stage of Life
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              From first home buyers to wealth building, we have the expertise to help you achieve your financial goals
            </Text>
            <HStack spacing={4} pt={4}>
              <Button as={RouterLink} to="/finance/contact" size="lg" bg="white" color={NAVY} _hover={{ bg: FINANCE_ACCENT, color: NAVY }} rightIcon={<FaArrowRight />} transition="all 0.2s">
                Get Started
              </Button>
              <Button as="a" href="#services" size="lg" variant="outline" borderColor="white" color="white" _hover={{ bg: FINANCE_ACCENT, borderColor: FINANCE_ACCENT, color: NAVY }} transition="all 0.2s">
                View Services
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={8}>
            <Stat textAlign="center">
              <StatNumber fontSize="4xl" fontWeight="bold" color={NAVY}>$72M</StatNumber>
              <StatLabel fontSize="md" color="gray.600">Loans Settled</StatLabel>
            </Stat>
            <Stat textAlign="center">
              <StatNumber fontSize="4xl" fontWeight="bold" color={FINANCE_ACCENT}>40+</StatNumber>
              <StatLabel fontSize="md" color="gray.600">Lenders</StatLabel>
            </Stat>
            <Stat textAlign="center">
              <StatNumber fontSize="4xl" fontWeight="bold" color={NAVY}>187</StatNumber>
              <StatLabel fontSize="md" color="gray.600">Loans Written</StatLabel>
            </Stat>
            <Stat textAlign="center">
              <StatNumber fontSize="4xl" fontWeight="bold" color={FINANCE_ACCENT}>151</StatNumber>
              <StatLabel fontSize="md" color="gray.600">Clients</StatLabel>
            </Stat>
            <Stat textAlign="center">
              <StatNumber fontSize="4xl" fontWeight="bold" color={NAVY}>4 days</StatNumber>
              <StatLabel fontSize="md" color="gray.600">Pre-Approval</StatLabel>
            </Stat>
            <Stat textAlign="center">
              <StatNumber fontSize="4xl" fontWeight="bold" color={FINANCE_ACCENT}>14-21 days</StatNumber>
              <StatLabel fontSize="md" color="gray.600">Full Approval</StatLabel>
            </Stat>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Service Categories */}
      <Container id="services" maxW="container.xl" py={20}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">Our Finance Services</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Comprehensive finance solutions tailored to your needs
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {serviceCategories.map((category, index) => (
            <Box
              key={index}
              bg="white"
              borderRadius="xl"
              border="1px"
              borderColor="gray.200"
              _hover={{ shadow: "xl", borderColor: FINANCE_ACCENT, transform: "translateY(-4px)" }}
              transition="all 0.3s"
              overflow="hidden"
            >
              <Box h="200px" overflow="hidden">
                <Box as="img" src={category.img} alt={category.imgAlt} w="full" h="full" objectFit="cover" />
              </Box>
              <Box p={8}>
                <Icon as={category.icon} boxSize={10} color={category.color} mb={4} />
                <Heading size="md" mb={3} color="gray.800">{category.title}</Heading>
                <Text color="gray.600" mb={6}>{category.description}</Text>
                <VStack align="stretch" spacing={2}>
                  {category.features.map((feature, idx) => (
                    <HStack key={idx} spacing={2}>
                      <Icon as={FaCheckCircle} color={category.color} />
                      <Text fontSize="sm" color="gray.700">{feature}</Text>
                    </HStack>
                  ))}
                </VStack>
                <Button as={RouterLink} to={category.link || "/finance/services"} mt={6} color={NAVY} variant="outline" borderColor={NAVY} rightIcon={<FaArrowRight />} w="full" _hover={{ bg: "#0f1538", borderColor: "#0f1538", color: "white" }} transition="all 0.2s">
                  Learn More
                </Button>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Why One Finance Australia */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Heading size="xl" color={NAVY}>We work for you. Not the banks.</Heading>
              <Text color="gray.600" fontSize="lg">
                One Finance Australia is a broker, not a bank. That means we search the full market to find the structure that suits your life — not the product that suits a lender's margin. We integrate your borrowing power with your property strategy and solar plans.
              </Text>
              <Button as={RouterLink} to="/contact" bg="#C2A878" color="#141C49" size="lg" rightIcon={<FaArrowRight />} _hover={{ bg: "#b09060" }} transition="all 0.2s">
                Book Consultation
              </Button>
            </VStack>
            <Flex direction="column" gap={6}>
              <Box borderRadius="xl" overflow="hidden" h="240px" position="relative" shadow="lg">
                <Box as="img" src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80" alt="Professional finance advisory conversation" w="full" h="full" objectFit="cover" />
                <Box position="absolute" bottom={0} left={0} right={0} p={4} bgGradient={`linear(to-t, ${NAVY}CC, transparent)`}>
                  <Text fontSize="xs" fontWeight="600" color="white" letterSpacing="wider" textTransform="uppercase">Structure before strategy</Text>
                </Box>
              </Box>
              <SimpleGrid columns={2} spacing={4}>
                {[
                  { label: "40+ Lenders", desc: "Access to the full market — not a panel of preferred partners." },
                  { label: "Integrated Strategy", desc: "Finance, property and solar working as one system." },
                  { label: "No Upfront Fees", desc: "We're paid by the lender. You pay nothing for our advice." },
                  { label: "Fast Pre-Approvals", desc: "Most clients receive pre-approval within 48 hours." },
                ].map((item) => (
                  <Box key={item.label} p={5} bg="white" borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.100">
                    <Text fontWeight="700" color={NAVY} mb={2} fontSize="sm">{item.label}</Text>
                    <Text fontSize="sm" color="gray.600">{item.desc}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Flex>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl" color="gray.800">Why Choose Our Finance Services?</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              We work for you, not the banks
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={6}>
            {benefits.map((benefit, index) => (
              <HStack
                key={index}
                p={6}
                bg="white"
                borderRadius="lg"
                spacing={4}
                border="1px"
                borderColor="gray.200"
              >
                <Icon as={FaCheckCircle} color={FINANCE_ACCENT} boxSize={6} flexShrink={0} />
                <Text fontWeight="500" color="gray.700">{benefit}</Text>
              </HStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={20}>
        <Box
          bg="#141C49"
          color="white"
          p={12}
          borderRadius="2xl"
          textAlign="center"
        >
          <Heading size="xl" mb={4}>Ready to Get Started?</Heading>
          <Text fontSize="lg" mb={8} color="gray.300">
            Speak with one of our finance experts today for a free consultation
          </Text>
          <HStack spacing={4} justify="center">
            <Button as={RouterLink} to="/finance/contact" size="lg" bg="white" color={NAVY} _hover={{ bg: "#0f1538", color: "white" }} transition="all 0.2s">
              Book Consultation
            </Button>
            <Button as={RouterLink} to="/finance/contact" size="lg" variant="outline" borderColor="white" color="white" _hover={{ bg: "#0f1538", borderColor: "#0f1538" }} transition="all 0.2s">
              Call Us Now
            </Button>
          </HStack>
        </Box>
      </Container>

      <GoogleReviews placeId={process.env.REACT_APP_GOOGLE_PLACE_ID_FINANCE || ""} />

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceHome;
